import "./company.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCompanies, updateCompany } from "../../redux/apiCalls";
import moment from "moment";
import styled from "styled-components";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import { FiPlus } from "react-icons/fi";

const statusOptions = [
    {
        label: "Inactive",
        value: "inactive",
    },
    {
        label: "Active",
        value: "active",
    },
    {
        label: "All",
        value: "",
    },
];

const ModalContainer = styled.div`
    height: 100%;
    width: 100%;
    opacity: 0.75;
    background: black;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    position: fixed;
    z-index: 100;
`;

const OverlayContainer = styled.div`
    overflow: hidden;
`;

const Overlay = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    color: white;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    top: 0;
    background-color: rgba(white, 0.5);
    visibility: visible;

    /* Add top padding on mobile to avoid overlap with navbar */
    @media (max-width: 768px) {
        padding-top: 80px;
    }
`;

const ModalDetails = styled.div`
    background-color: rgba(white, 0.5);
    color: black;
    width: 50%;
    max-height: 90vh;
    display: flex;
    z-index: 102;
    overflow: hidden;

    /* Make the popup wider on small screens without affecting desktop */
    @media (max-width: 768px) {
        width: 90%;
        max-height: 85vh;
    }
`;

const ModalItemContainer = styled.div`
    background-color: white;
    color: black;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    
    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    & .updateTitle {
        padding: 20px;
    }

    & .addProductForm {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    & .addProductItem {
        width: 100%;
    }

    & .addProductItem label {
        display: block;
        margin-bottom: 5px;
        font-weight: 600;
        color: #374151;
    }

    & .addProductItem input,
    & .addProductItem select,
    & .addProductItem textarea {
        width: 100%;
        box-sizing: border-box;
        padding: 10px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
    }

    & .addProductItem input:focus,
    & .addProductItem select:focus,
    & .addProductItem textarea:focus {
        outline: none;
        border-color: #f97316;
        box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
    }

    & .updateProductButton {
        margin-top: 10px;
        margin-bottom: 20px;
        padding: 12px 20px;
        background-color: #000;
        color: white;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s;
    }

    & .updateProductButton:hover {
        background-color: #374151;
    }

    & .react-select-container {
        width: 100%;
    }

    /* Improve mobile spacing and input widths inside the modal only */
    @media (max-width: 768px) {
        & .updateTitle {
            padding: 12px;
        }

        & .addProductForm {
            gap: 12px;
        }

        & .addProductItem input,
        & .addProductItem select,
        & .addProductItem textarea {
            padding: 12px;
            font-size: 16px;
        }
    }
`;

export default function Company() {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);
    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );
    
    // Determine principle parameter based on user type
    const getPrincipleParam = () => {
        if (superadmin) {
            // SuperAdmin can see all companies
            return undefined;
        } else if (currentUser?.type === "outright" || currentUser?.type === "manager") {
            // Outright and manager users can only see their own principle
            return currentUser._id;
        } else {
            // For other user types, use their principle if available
            return currentUser?.principle;
        }
    };

    const [inputs, setInputs] = useState({
        status: undefined,
        type: "user",
        limit: 20,
        page: 1,
        name: undefined,
        principle: getPrincipleParam(),
    });
    const [company, setCompany] = useState(undefined);
    const [name, setName] = useState(undefined);
    const [editModalState, setEditModalState] = useState(false);
    const [companiesData, setCompaniesData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        // Update principle in inputs when currentUser changes
        const principleParam = getPrincipleParam();
        setInputs((prev) => {
            // Only update if principle actually changed to avoid unnecessary re-renders
            if (prev.principle !== principleParam) {
                return {
                    ...prev,
                    principle: principleParam,
                };
            }
            return prev;
        });
    }, [currentUser?._id, currentUser?.principle, currentUser?.type, superadmin]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await getCompanies(dispatch, inputs);
                setCompaniesData(result ? result.companies : []);
                setTotalPages(result.totalPages);
                setIsLoaded(true);
            } catch (error) {
                console.error("Error fetching companies:", error);
            }
        }

        fetchData();
    }, [dispatch, inputs]);

    const handleChange = (e) => {
        setCompany((prev) => {
            return { ...prev, [e.target.name]: e.target.value };
        });
    };

    const handleAddressChange = (e) => {
        setCompany((prev) => {
            return {
                ...prev,
                address: {
                    ...prev.address,
                    [e.target.name]: e.target.value
                }
            };
        });
    };

    const handleNameChange = (e) => {
        setIsLoaded(false);
        setInputs((prev) => {
            return { ...prev, page: 1, name: e.target.value };
        });
        setName(e.target.value);
    };

    const handleInputStatus = (e) => {
        setIsLoaded(false);
        setInputs((prev) => {
            return { ...prev, page: 1, status: e.value };
        });
    };

    const handleResetFilters = () => {
        const principleParam = getPrincipleParam();
        setInputs({
            status: undefined,
            type: "user",
            limit: 20,
            page: 1,
            name: undefined,
            principle: principleParam,
        });
        setName(undefined);
        setIsLoaded(false);
    };

    const handleClick = async () => {
        updateCompany(company._id, company, dispatch);
    };

    const columns = [
        {
            Header: "Creditor Code",
            accessor: "debtorCode",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "Name",
            accessor: "name",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value}</div>
            ),
        },
        {
            Header: "Phone Number",
            accessor: "phoneNumber",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value}</div>
            ),
        },
        {
            Header: "Registration Number",
            accessor: "ic",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "TIN",
            accessor: "tin",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "Email",
            accessor: "email",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "Address",
            accessor: "address.lineOne",
            Cell: ({ row }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.original.address?.lineOne || ""}
                </div>
            ),
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{value}</div>
            ),
        },
        {
            Header: "Member Since",
            accessor: "createdAt",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {moment(value).format("DD/MM/YYYY")}
                </div>
            ),
        },
        {
            Header: "Action",
            accessor: "action",
            Cell: (params) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                        onClick={() => {
                            setCompany(params.row.original);
                            window.scrollTo(0, 0);
                            setEditModalState(true);
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                    >
                        Edit
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="container mx-auto px-4 py-8 block">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={handleResetFilters}
                    className="flex items-center bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
                >
                    <FiPlus className="mr-2" />
                    Reset Filters
                </button>
            </div>

            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name
                        </label>
                        <input
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            name="name"
                            value={name}
                            type="text"
                            placeholder="Search by name"
                            onChange={handleNameChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </label>
                        <div className="react-select-container">
                            <Select
                                options={statusOptions.map((status, i) => {
                                    return {
                                        label: status.label,
                                        value: status.value,
                                    };
                                })}
                                onChange={handleInputStatus}
                                placeholder="Select Status"
                                {...commonSelectProps}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-orange-400">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.Header}
                                    className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider"
                                >
                                    {column.Header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {companiesData ? companiesData.map((comp) => (
                            <tr key={comp._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.debtorCode ? comp.debtorCode : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.phoneNumber}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.ic ? comp.ic : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.tin ? comp.tin : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.email ? comp.email : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {comp.address?.lineOne || ""}, {comp.address?.lineTwo || ""}, {comp.address?.city || ""}, {comp.address?.zip || ""}, {comp.address?.country || ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                    {comp.status}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {moment(comp.createdAt).format("DD/MM/YYYY")}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <button
                                        onClick={() => {
                                            const { password, ...restCompany } = comp;
                                            setCompany(restCompany);
                                            window.scrollTo(0, 0);
                                            setEditModalState(true);
                                        }}
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                                    >
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                                    Loading...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                
                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="order-list-table-footer px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="order-list-table-footer-inner flex-1 flex justify-between items-center">
                            <div className="order-list-table-footer-results text-sm text-gray-700">
                                Showing{' '}
                                <span className="font-medium">
                                    {((inputs.page - 1) * inputs.limit) + 1}
                                </span>
                                {' '}-{' '}
                                <span className="font-medium">
                                    {Math.min(inputs.page * inputs.limit, totalPages)}
                                </span>
                                {' '}of{' '}
                                <span className="font-medium">{totalPages}</span>
                                {' '}results
                            </div>
                            <div className="order-list-table-footer-pagination flex space-x-2">
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: 1 }))}
                                    disabled={inputs.page === 1}
                                    title="First"
                                    aria-label="First page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page === 1
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;&lt;</span>
                                    <span className="order-list-pagination-text">First</span>
                                </button>
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={inputs.page === 1}
                                    title="Previous"
                                    aria-label="Previous page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page === 1
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;</span>
                                    <span className="order-list-pagination-text">Previous</span>
                                </button>
                                <span className="order-list-pagination-page px-3 py-1 bg-gray-100 text-gray-900 rounded-md text-sm font-medium">
                                    Page {inputs.page} of {Math.ceil(totalPages / inputs.limit)}
                                </span>
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={inputs.page >= Math.ceil(totalPages / inputs.limit)}
                                    title="Next"
                                    aria-label="Next page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page >= Math.ceil(totalPages / inputs.limit)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;</span>
                                    <span className="order-list-pagination-text">Next</span>
                                </button>
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: Math.ceil(totalPages / inputs.limit) }))}
                                    disabled={inputs.page >= Math.ceil(totalPages / inputs.limit)}
                                    title="Last"
                                    aria-label="Last page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page >= Math.ceil(totalPages / inputs.limit)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;&gt;</span>
                                    <span className="order-list-pagination-text">Last</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editModalState && (
                <OverlayContainer>
                    <ModalContainer
                        onClick={() => setEditModalState(false)}
                    ></ModalContainer>
                    <Overlay>
                        <ModalDetails>
                            <ModalItemContainer>
                                <div className="updateTitle">
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '20px',
                                        paddingBottom: '15px',
                                        borderBottom: '2px solid #f3f4f6'
                                    }}>
                                        <h2 style={{ 
                                            fontSize: '1.5rem', 
                                            fontWeight: '700',
                                            color: '#111827',
                                            margin: 0 
                                        }}>Edit Company</h2>
                                        <button
                                            onClick={() => setEditModalState(false)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '1.5rem',
                                                cursor: 'pointer',
                                                color: '#6b7280',
                                                padding: '5px 10px',
                                                lineHeight: 1
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <form className="addProductForm">
                                        <div className="addProductItem">
                                                    <label>Creditor Code</label>
                                                    <input
                                                        name="debtorCode"
                                                        type="text"
                                                        placeholder={company.debtorCode || "Creditor Code"}
                                                        value={company.debtorCode ?? ""}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Full Name</label>
                                                    <input
                                                        name="name"
                                                        type="text"
                                                        placeholder={company.name}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Email</label>
                                                    <input
                                                        name="email"
                                                        type="text"
                                                        placeholder={company.email}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Phone Number</label>
                                                    <input
                                                        name="phoneNumber"
                                                        type="text"
                                                        placeholder={company.phoneNumber}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>IC / Company Registration</label>
                                                    <input
                                                        name="ic"
                                                        type="text"
                                                        placeholder={company.ic || "IC / Company Registration Number"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>TIN</label>
                                                    <input
                                                        name="tin"
                                                        type="text"
                                                        placeholder={company.tin || "Tax Identification Number"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Address Line 1 *</label>
                                                    <input
                                                        name="lineOne"
                                                        type="text"
                                                        placeholder={company.address?.lineOne || "Address Line 1"}
                                                        onChange={handleAddressChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Address Line 2</label>
                                                    <input
                                                        name="lineTwo"
                                                        type="text"
                                                        placeholder={company.address?.lineTwo || "Address Line 2"}
                                                        onChange={handleAddressChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>City *</label>
                                                    <input
                                                        name="city"
                                                        type="text"
                                                        placeholder={company.address?.city || "City"}
                                                        onChange={handleAddressChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Zip Code *</label>
                                                    <input
                                                        name="zip"
                                                        type="text"
                                                        placeholder={company.address?.zip || "Zip Code"}
                                                        onChange={handleAddressChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Country *</label>
                                                    <input
                                                        name="country"
                                                        type="text"
                                                        placeholder={company.address?.country || "Country"}
                                                        onChange={handleAddressChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>
                                                        Date of Birth (optional)
                                                    </label>
                                                    <input
                                                        name="dob"
                                                        type="date"
                                                        value={
                                                            company.dob
                                                                ? moment(
                                                                      company.dob
                                                                  ).format("YYYY-MM-DD")
                                                                : undefined
                                                        }
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                        <div
                                            onClick={handleClick}
                                            className="updateProductButton"
                                        >
                                            Update Company
                                        </div>
                                    </form>
                                </div>
                            </ModalItemContainer>
                        </ModalDetails>
                    </Overlay>
                </OverlayContainer>
            )}
        </div>
    );
}

